import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import Campo from "../posgrados/Campo";
import Modalidad from "../posgrados/Modalidad";

const BASE_URL = "https://be-ofertas.utpl.edu.ec";

const programaMap = {
  maestria: "Maestría",
  especializacion: "Especialización",
  doctorado: "Doctorado",
};

const modalidadMap = {
  en_linea: "En línea",
  presencial: "Presencial",
  hibrida: "Híbrida",
};

const facultadMap = {
  facultad_de_ciencias_economicas_y_empresariales:
    "Ciencias Económicas y Empresariales",
  facultad_de_ciencias_exactas_y_naturales: "Ciencias Exactas y Naturales",
  facultad_de_ciencias_de_la_salud: "Ciencias de la Salud",
  facultad_de_ciencias_sociales_educacion_y_humanidades:
    "Ciencias Sociales, Educación y Humanidades",
  facultad_de_ciencias_juridicias_y_politicas: "Ciencias Jurídicas y Políticas",
  facultad_de_ingenierias_y_arquitectura: "Ingenierías y Arquitectura",
  escuela_de_negocios: "UTPL Escuela de Negocios",
};

const badgeColors = {
  Maestría: "bg-blue-100 text-blue-800 border border-blue-200",
  Especialización: "bg-purple-100 text-purple-800 border border-purple-200",
  Doctorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const getImageUrl = (node, included, fieldName) => {
  const mediaId = node.relationships?.[fieldName]?.data?.id;
  if (!mediaId) return null;

  const media = included.find(
    (item) => item.type === "media--image" && item.id === mediaId
  );
  const fileId = media?.relationships?.field_media_image?.data?.id;
  if (!fileId) return null;

  const file = included.find(
    (item) => item.type === "file--file" && item.id === fileId
  );

  return file?.attributes?.uri?.url || null;
};

const cleanText = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

function OfferTecnologiasSemestral() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint =
          `${BASE_URL}/jsonapi/node/tecnologias?` +
          "fields[node--tecnologias]=title,field_tipo_de_programa,field_facultad,field_modalidad,field_duracion,field_inicio_clases,field_campo_amplio,field_url_al_contenido,field_imagen_portada,field_tec_semestral" +
          "&fields[media--image]=field_media_image" +
          "&fields[file--file]=uri" +
          "&include=field_imagen_portada,field_imagen_portada.field_media_image" +
          "&filter[field_tec_semestral]=1"; // Se cambia =true por =1 para mayor compatibilidad con Drupal

        const urls = [endpoint, `${endpoint}&page[offset]=50&page[limit]=50`];

        const responses = await Promise.all(urls.map((url) => fetch(url)));
        const jsons = await Promise.all(responses.map((r) => r.json()));

        const allItems = jsons.flatMap((json) => json.data || []);
        const allIncluded = jsons.flatMap((json) => json.included || []);

        const uniqueItems = [
          ...new Map(allItems.map((item) => [item.id, item])).values(),
        ];

        // Filtro extra de seguridad en JavaScript
        const semestralItems = uniqueItems.filter(
          (item) =>
            item.attributes?.field_tec_semestral === true ||
            item.attributes?.field_tec_semestral === 1 ||
            item.attributes?.field_tec_semestral === "1"
        );

        const transformed = semestralItems.map((item) => {
          const attrs = item.attributes;
          const imagePath = getImageUrl(
            item,
            allIncluded,
            "field_imagen_portada"
          );

          return {
            id: item.id,
            title: attrs.title,
            field_programa:
              programaMap[attrs.field_tipo_de_programa] ||
              attrs.field_tipo_de_programa,
            field_facultad:
              facultadMap[attrs.field_facultad] || attrs.field_facultad,
            field_modalidad:
              modalidadMap[attrs.field_modalidad] || attrs.field_modalidad,
            field_duracion: attrs.field_duracion || "",
            field_inicio_clases: attrs.field_inicio_clases || "",
            field_campo_amplio: attrs.field_campo_amplio || "",
            field_url: attrs.field_url_al_contenido || "",
            field_imagen_portada: imagePath
              ? BASE_URL + imagePath
              : "https://via.placeholder.com/600x400",
          };
        });

        // Ordenar por fecha de inicio y título
        transformed.sort((a, b) => {
          const dateA = a.field_inicio_clases || "";
          const dateB = b.field_inicio_clases || "";
          const dateComparison = dateB.localeCompare(dateA, "es");

          if (dateComparison !== 0) return dateComparison;

          return cleanText(a.title).localeCompare(cleanText(b.title), "es");
        });

        setData(transformed);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading)
    return <p className="px-16 md:px-10 py-6">Cargando tecnologías...</p>;

  if (error)
    return <p className="px-16 md:px-10 py-6 text-red-500">Error: {error}</p>;

  return (
    <div className="px-16 md:px-10">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {data.map((item) => (
          <motion.a
            key={item.id}
            href={item.field_url}
            rel="noreferrer"
            className="group relative block bg-white rounded-2xl shadow-md overflow-hidden"
            initial="rest"
            animate="rest"
            whileHover="hover"
          >
            <div className="h-40 overflow-hidden">
              <img
                src={item.field_imagen_portada}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </div>

            <motion.div
              variants={{
                rest: { scale: 1, opacity: 0.8 },
                hover: { scale: 1.2, opacity: 1 },
              }}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-md rounded-full p-2 shadow-md"
            >
              <Plus size={12} />
            </motion.div>

            <div className="p-5 space-y-3">
      

              <h3 className="text-base font-bold leading-tight p-0">
                {item.title}
              </h3>

              {item.field_modalidad && (
                <div
                  className="
                    [&_p]:text-sm 
                    [&_p]:text-gray-600 
                    [&_p]:p-0
                    [&_p]:inline-flex
                    [&_p]:leading-none
                    [&_ul]:p-0
                    [&_ul]:m-0
                    [&_li]:text-sm 
                    [&_li]:text-gray-600 
                    [&_li]:py-0      
                    [&_li]:pt-1           
                    [&_li]:leading-none"
                >
                  <p>
                    <strong>Modalidad: </strong>
                  </p>
                  <Modalidad value={item.field_modalidad} />
                </div>
              )}

              {item.field_duracion && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Duración:</strong>{" "}
                  {item.field_duracion
                    .replaceAll("_", " ")
                    .replace("anos", "años")
                    .replace("ano", "año")}
                </p>
              )}

              {item.field_facultad && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Facultad:</strong> {item.field_facultad}
                </p>
              )}

              {item.field_campo_amplio && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Campo: </strong>
                  <Campo value={item.field_campo_amplio} />
                </p>
              )}

              {item.field_inicio_clases && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Inicio de clases:</strong> {item.field_inicio_clases}
                </p>
              )}
            </div>

            <motion.div
              variants={{
                rest: { opacity: 0 },
                hover: { opacity: 1 },
              }}
              className="absolute inset-0 bg-black/10 flex items-center justify-center"
            >
              <ArrowRight size={44} className="text-white" />
            </motion.div>
          </motion.a>
        ))}
      </div>
    </div>
  );
}

export default OfferTecnologiasSemestral;